/**
 * Orders — Stage durations percentiles. Stage 7.
 * Sources `/api/bi/orders/funnel/durations/`.
 */
import { useMemo } from 'react';
import { Timer } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { useOrdersDurations } from '@/api/bi';
import { rangeISO, useFilters } from '@/state/filters';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

const STAGE_LABEL: Record<string, string> = {
  created_to_acknowledged: 'Created → Assigned',
  acknowledged_to_packed: 'Assigned → Packed',
  packed_to_billed: 'Packed → Billed',
  billed_to_picked: 'Billed → Picked',
  picked_to_completed: 'Picked → Completed',
};

export function DurationsPage() {
  const range = useFilters((s) => s.range);
  const storeIds = useFilters((s) => s.storeIds);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const { start: startISO, end: endISO } = useMemo(
    () => rangeISO(range, timeRangeMode),
    [range, timeRangeMode],
  );

  const query = useOrdersDurations({ start: startISO, end: endISO, storeIds });
  const stages = query.data?.stages ?? [];

  // Find global max for relative bar widths so stages stay comparable.
  const globalMax = Math.max(
    1,
    ...stages.flatMap((s) => [s.p99 ?? 0, s.p95 ?? 0, s.p90 ?? 0]),
  );

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="Stage durations"
        description="P10/P50/P90/P95/P99 between every two transitions in the order funnel. Means lie; percentiles tell the truth."
      />

      <Card className="mt-4">
        <CardBody>
          {query.isError ? (
            <ErrorState
              description="Couldn't load stage durations."
              onRetry={() => void query.refetch()}
            />
          ) : query.isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-20 animate-pulse rounded-md bg-muted/40" />
              ))}
            </div>
          ) : stages.length === 0 ? (
            <EmptyState
              icon={<Timer className="h-5 w-5" />}
              title="No completed orders in this window"
              description="Stage durations are measured on delivered orders. Widen the date range or clear the store filter."
            />
          ) : (
            <div className="space-y-5">
              {stages.map((s) => (
                <StageRow
                  key={s.stage}
                  label={STAGE_LABEL[s.stage] ?? s.stage}
                  samples={s.samples}
                  mean={s.mean}
                  p10={s.p10}
                  p50={s.p50}
                  p90={s.p90}
                  p95={s.p95}
                  p99={s.p99}
                  scaleMax={globalMax}
                />
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      <p className="mt-3 text-xs text-muted-foreground">
        Bars scale to the largest P99 across all stages — that way an outlier stage stays visually
        loud without redrawing the chart per row.
      </p>
    </>
  );
}

function StageRow({
  label,
  samples,
  mean,
  p10,
  p50,
  p90,
  p95,
  p99,
  scaleMax,
}: {
  label: string;
  samples: number;
  mean: number | null;
  p10: number | null;
  p50: number | null;
  p90: number | null;
  p95: number | null;
  p99: number | null;
  scaleMax: number;
}) {
  const lo = p10 ?? 0;
  const med = p50 ?? lo;
  const hi = p90 ?? med;
  const xhi = p95 ?? hi;
  const xxhi = p99 ?? xhi;

  const pct = (v: number) => Math.max(0, Math.min(100, (v / scaleMax) * 100));

  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between text-xs">
        <span className="font-medium text-foreground">{label}</span>
        <span className="text-muted-foreground">
          n={fmt.number(samples)} · mean {fmt.duration(mean ?? 0)}
        </span>
      </div>
      <div className="relative h-6 rounded-full bg-muted/40">
        <div
          className="absolute top-0 h-full rounded-full bg-primary/15"
          style={{ left: `${pct(lo)}%`, width: `${pct(hi - lo)}%` }}
          title={`P10–P90: ${fmt.duration(lo)} – ${fmt.duration(hi)}`}
        />
        <div
          className="absolute top-0 h-full rounded-full bg-primary/30"
          style={{ left: `${pct(hi)}%`, width: `${pct(xhi - hi)}%` }}
          title={`P90–P95: ${fmt.duration(hi)} – ${fmt.duration(xhi)}`}
        />
        <div
          className="absolute top-0 h-full rounded-full bg-danger/30"
          style={{ left: `${pct(xhi)}%`, width: `${pct(xxhi - xhi)}%` }}
          title={`P95–P99: ${fmt.duration(xhi)} – ${fmt.duration(xxhi)}`}
        />
        {/* Median tick */}
        <div
          className="absolute top-0 h-full w-0.5 bg-primary"
          style={{ left: `${pct(med)}%` }}
          title={`Median: ${fmt.duration(med)}`}
        />
      </div>
      <div className="mt-1 flex flex-wrap items-baseline gap-3 text-[11px] text-muted-foreground">
        <PercentilePill label="P10" value={p10} />
        <PercentilePill label="P50" value={p50} highlight />
        <PercentilePill label="P90" value={p90} />
        <PercentilePill label="P95" value={p95} />
        <PercentilePill label="P99" value={p99} tone="danger" />
      </div>
    </div>
  );
}

function PercentilePill({
  label,
  value,
  highlight,
  tone = 'neutral',
}: {
  label: string;
  value: number | null;
  highlight?: boolean;
  tone?: 'neutral' | 'danger';
}) {
  return (
    <span className={cn('inline-flex items-baseline gap-1', highlight && 'text-foreground font-medium')}>
      <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground/80">
        {label}
      </span>
      <span
        className={cn(
          'kpi-num text-xs',
          tone === 'danger' ? 'text-danger' : 'text-foreground',
        )}
      >
        {value != null ? fmt.duration(value) : '—'}
      </span>
    </span>
  );
}
